import React, { useState, useRef, useEffect, useCallback } from 'react';

interface FilterableSelectProps {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * A select-like input that lets users type to filter a long list of options.
 * Used in the allocation sidebar for grouping dropdowns with many values.
 */
const FilterableSelect: React.FC<FilterableSelectProps> = ({ value, options, onChange, placeholder = '—', className }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on click-outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = filter
    ? options.filter(o => o.toLowerCase().includes(filter.toLowerCase()))
    : options;

  const handleSelect = useCallback((opt: string) => {
    onChange(opt);
    setOpen(false);
    setFilter('');
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setFilter('');
  }, [onChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
    if (!open) setOpen(true);
  }, [open]);

  const handleFocus = useCallback(() => {
    setOpen(true);
    setFilter('');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setFilter('');
      inputRef.current?.blur();
    }
  }, []);

  return (
    <div className={`filterable-select ${className || ''}`} ref={containerRef}>
      <input
        ref={inputRef}
        className="filterable-select-input"
        type="text"
        value={open ? filter : value}
        placeholder={value || placeholder}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <div className="filterable-select-dropdown">
          <div
            className={`filterable-select-option ${!value ? 'selected' : ''}`}
            onClick={handleClear}
          >
            —
          </div>
          {filtered.map(opt => (
            <div
              key={opt}
              className={`filterable-select-option ${opt === value ? 'selected' : ''}`}
              onClick={() => handleSelect(opt)}
            >
              {opt}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="filterable-select-empty">—</div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterableSelect;
